import { SITE_NAME } from "../constants/brand";
import { SHOPS } from "../constants/shopMeta";

const PRODUCT_DESCRIPTION_LIMIT = 158;

const SEO_TERMS = Object.freeze([
  "costume supplies",
  "costume fabrics",
  "haberdashery",
  "textiles",
  "ready-made costumes",
  "replica garments",
  "vintage trims",
  "sewing supplies",
]);

const safeText = (value = "") => String(value || "").replace(/\s+/g, " ").trim();

const toFiniteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const getName = (value) => {
  if (!value) return "";
  if (typeof value === "string") return safeText(value);
  if (typeof value === "object") {
    return safeText(value.name || value.title || value.label || value.slug);
  }
  return "";
};

export function cleanSeoText(value = "", maxLength = PRODUCT_DESCRIPTION_LIMIT) {
  const text = safeText(value);

  if (!text || text.length <= maxLength) return text;

  const clipped = text.slice(0, maxLength - 1).trimEnd();
  const lastSpace = clipped.lastIndexOf(" ");

  return `${clipped.slice(0, lastSpace > 80 ? lastSpace : clipped.length)}…`;
}

export function getSeoShopName(shopKey = "") {
  const normalized = safeText(shopKey).toLowerCase();
  const shop = SHOPS.find((entry) => entry.shopKey === normalized);

  if (shop?.name) return shop.name;
  if (normalized === "textile") return "Textiles";
  if (normalized === "standard") return "Ready-Made";

  return "Costume Collection";
}

function getProductCategory(product = {}, shopName = "") {
  return (
    getName(product.category) ||
    getName(product.subCategory) ||
    getName(product.subcategory) ||
    safeText(product.categoryName) ||
    safeText(product.subCategoryName) ||
    shopName
  );
}

function getOptionValue(selectedOption = {}, keys = []) {
  const value = selectedOption?.value || {};

  for (const key of keys) {
    const candidate = safeText(selectedOption?.[key] || value?.[key]);
    if (candidate) return candidate;
  }

  return "";
}

export function buildProductSeoTitle({
  product = {},
  selectedOption = null,
  shopKey = "",
}) {
  const productName = safeText(product.name) || "Product";
  const shopName = getSeoShopName(
    product.shopKey || shopKey || product.productType,
  );
  const optionLabel = safeText(selectedOption?.label);
  const titleBase =
    optionLabel && !productName.toLowerCase().includes(optionLabel.toLowerCase())
      ? `${productName} - ${optionLabel}`
      : productName;

  return cleanSeoText(`${titleBase} | ${shopName} | ${SITE_NAME}`, 70);
}

export function buildProductSeoDescription({
  product = {},
  selectedOption = null,
  shopKey = "",
}) {
  const productName = safeText(product.name) || "this product";
  const shopName = getSeoShopName(
    product.shopKey || shopKey || product.productType,
  );
  const brand = safeText(product.brand) || SITE_NAME;
  const category = getProductCategory(product, shopName);
  const sourceDescription = safeText(
    product.metaDescription ||
      product.seoDescription ||
      product.description ||
      product.shortDescription,
  );

  if (sourceDescription) {
    const suffix = sourceDescription.includes(SITE_NAME)
      ? ""
      : ` Shop ${category.toLowerCase()} at ${SITE_NAME}.`;

    return cleanSeoText(`${sourceDescription}${suffix}`);
  }

  const optionLabel = safeText(selectedOption?.label);
  const optionCopy = optionLabel ? ` in ${optionLabel}` : "";

  return cleanSeoText(
    `Shop ${productName}${optionCopy} by ${brand} at ${SITE_NAME}. Discover ${category.toLowerCase()} for costume, textile, wardrobe and haberdashery projects.`,
  );
}

export function buildProductSeoKeywords({
  product = {},
  selectedOption = null,
  shopKey = "",
}) {
  const shopName = getSeoShopName(
    product.shopKey || shopKey || product.productType,
  );
  const keywords = [
    SITE_NAME,
    safeText(product.name),
    safeText(product.brand),
    shopName,
    getProductCategory(product, shopName),
    safeText(product.sku),
    getOptionValue(selectedOption, ["sku", "variantSku"]),
    getOptionValue(selectedOption, ["colour", "color"]),
    getOptionValue(selectedOption, ["material"]),
    ...SEO_TERMS,
  ];

  const seen = new Set();

  return keywords
    .filter(Boolean)
    .filter((keyword) => {
      const key = keyword.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .join(", ");
}

export function buildProductSeo({
  product = {},
  selectedOption = null,
  selectedPrice = 0,
  gallery = [],
  hasStock = false,
  url = "",
  shopKey = "",
}) {
  const shopName = getSeoShopName(
    product.shopKey || shopKey || product.productType,
  );
  const category = getProductCategory(product, shopName);
  const optionSku = getOptionValue(selectedOption, ["sku", "variantSku"]);
  const price = Number(selectedPrice);
  const available = hasStock
    ? Math.max(
        1,
        toFiniteNumber(
          selectedOption?.maxAddableQty ||
            selectedOption?.remainingAddableQty ||
            selectedOption?.available ||
            product.available,
          1,
        ),
      )
    : 0;

  return {
    ...product,
    name: safeText(product.name),
    brand: safeText(product.brand) || SITE_NAME,
    description: buildProductSeoDescription({
      product,
      selectedOption,
      shopKey,
    }),
    sku: optionSku || safeText(product.sku),
    productCode: safeText(product.productCode || product.code),
    price: Number.isFinite(price) ? price : 0,
    currency: safeText(product.currency) || "GBP",
    available,
    availability: available,
    availabilityUrl: hasStock
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock",
    image: gallery[0]?.url || product.defaultImage,
    images: gallery.map((image) => image.url).filter(Boolean),
    url,
    category,
    shopName,
    color:
      getOptionValue(selectedOption, ["colour", "color"]) ||
      safeText(product.colour || product.color),
    material:
      getOptionValue(selectedOption, ["material"]) || safeText(product.material),
    size: getOptionValue(selectedOption, ["size"]) || safeText(product.size),
  };
}
